import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type AskFn = (opts: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<AskFn>(() => Promise.resolve(false));

export function useConfirm(): AskFn {
  return useContext(Ctx);
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const ask = useCallback<AskFn>((o) => {
    return new Promise<boolean>((resolve) => {
      // If a previous dialog is still open, resolve it false before opening.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setOpts(o);
    });
  }, []);

  const close = useCallback((result: boolean) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setOpts(null);
  }, []);

  useEffect(() => {
    if (!opts) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [opts, close]);

  return (
    <Ctx.Provider value={ask}>
      {children}
      {opts && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
          onClick={() => close(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            className="bg-brand-dark text-white rounded-2xl shadow-card max-w-sm w-full p-6 flex flex-col gap-4"
          >
            {opts.title && (
              <h2 className="text-lg font-semibold">{opts.title}</h2>
            )}
            <p className="text-sm text-white/80 whitespace-pre-line">
              {opts.message}
            </p>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => close(false)}
                className="flex-1 rounded-pill bg-white/10 text-white py-2 font-semibold"
              >
                {opts.cancelLabel ?? 'Annuler'}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => close(true)}
                className={`flex-1 rounded-pill text-white py-2 font-semibold ${
                  opts.danger ? 'bg-red-500' : 'bg-brand-orange'
                }`}
              >
                {opts.confirmLabel ?? 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
